import { ALL_PLANS, type PlanRow } from '../pricing/plans.js';

interface PricingStructuredDataProps {
  locale: 'fr' | 'en';
}

function planName(plan: PlanRow, locale: 'fr' | 'en'): string {
  const suffix = `${plan.visitsPerMonth}×`;
  if (plan.segment === 'solo') return `Solo ${suffix}`;
  if (plan.segment === 'duo') return `Duo ${suffix}`;
  return locale === 'fr' ? `Foyer ${suffix}` : `Household ${suffix}`;
}

function planDescription(plan: PlanRow, locale: 'fr' | 'en'): string {
  const fr = locale === 'fr';
  const people =
    plan.segment === 'solo'
      ? fr
        ? '1 personne'
        : '1 person'
      : plan.segment === 'duo'
        ? fr
          ? '2 personnes'
          : '2 people'
        : fr
          ? '3 à 5 personnes'
          : '3 to 5 people';
  const visits = fr
    ? `${plan.visitsPerMonth} ${plan.visitsPerMonth === 1 ? 'passage' : 'passages'} par mois`
    : `${plan.visitsPerMonth} ${plan.visitsPerMonth === 1 ? 'visit' : 'visits'} per month`;
  const service = fr ? 'laveuse à domicile' : 'in-home laundry service';
  return `${people} · ${visits} · ${service}`;
}

/** Machine-readable twin of the six-plan visual pricing ladder. */
export function PricingStructuredData({ locale }: PricingStructuredDataProps) {
  const offers = ALL_PLANS.map((plan) => ({
    '@type': 'Offer',
    name: planName(plan, locale),
    description: planDescription(plan, locale),
    priceCurrency: 'XOF',
    price: plan.amountXof,
    availability:
      plan.status === 'live' ? 'https://schema.org/InStock' : 'https://schema.org/PreOrder',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price: plan.amountXof,
      priceCurrency: 'XOF',
      referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitCode: 'MON' },
    },
  }));

  const data = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    '@id': 'https://washedafrica.com/#service',
    name: 'Washed',
    url: 'https://washedafrica.com/tarifs',
    serviceType:
      locale === 'fr' ? 'Abonnement de laveuse à domicile' : 'In-home laundry subscription',
    description:
      locale === 'fr'
        ? 'Formules mensuelles de laveuse à domicile à Lomé pour une personne, deux personnes ou trois à cinq personnes à la même adresse (famille, colocataires ou amis), avec un ou deux passages par mois.'
        : 'Monthly in-home laundry plans in Lomé for one person, two people, or three to five people at one address (family, flatmates or friends), with one or two visits per month.',
    inLanguage: locale,
    areaServed: {
      '@type': 'City',
      name: 'Lomé',
      address: { '@type': 'PostalAddress', addressCountry: 'TG' },
    },
    provider: { '@id': 'https://washedafrica.com/#organization' },
    offers,
  };

  return (
    <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(data) }} />
  );
}
